import React, { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../../firebase";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { initFCM } from "../../fcmService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [adminSettings, setAdminSettings] = useState({ postingPermissions: null, dashboardButtons: null });
  const [adminSettingsLoading, setAdminSettingsLoading] = useState(true);
  const prevRoleRef = useRef(null);
  const fcmInitializedRef = useRef(false);
  const adminSettingsInitializedRef = useRef(false);
  const adminUnsubsRef = useRef([]);

  const resetAdminSettings = () => {
    adminUnsubsRef.current.forEach((unsub) => unsub());
    adminUnsubsRef.current = [];
    adminSettingsInitializedRef.current = false;
    setAdminSettings({ postingPermissions: null, dashboardButtons: null });
    setAdminSettingsLoading(true);
  };

  const loadAdminSettings = useCallback(() => {
    if (adminSettingsInitializedRef.current) return;
    adminSettingsInitializedRef.current = true;

    let permLoaded = false;
    let layoutLoaded = false;
    const checkDone = () => {
      if (permLoaded && layoutLoaded) setAdminSettingsLoading(false);
    };

    const permRef = doc(db, "admin_settings", "posting_permissions");
    const unsubPerm = onSnapshot(permRef, (snap) => {
      setAdminSettings((prev) => ({ ...prev, postingPermissions: snap.exists() ? snap.data() : {} }));
      permLoaded = true;
      checkDone();
    }, () => {
      permLoaded = true;
      checkDone();
    });

    const layoutRef = doc(db, "admin_settings", "dashboard_layout");
    const unsubLayout = onSnapshot(layoutRef, (snap) => {
      setAdminSettings((prev) => ({ ...prev, dashboardButtons: snap.exists() ? (snap.data().buttons || null) : null }));
      layoutLoaded = true;
      checkDone();
    }, () => {
      layoutLoaded = true;
      checkDone();
    });

    adminUnsubsRef.current = [unsubPerm, unsubLayout];
  }, []);

  useEffect(() => {
    let unsubscribeSnapshot = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setUserRole(null);
        setUserData(null);
        prevRoleRef.current = null;
        fcmInitializedRef.current = false;
        setAuthLoading(false);
        resetAdminSettings();
        if (unsubscribeSnapshot) unsubscribeSnapshot();
        return;
      }
      const ref = doc(db, "users", user.uid);
      unsubscribeSnapshot = onSnapshot(ref, async (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.isVIP && data.vipExpiresAt) {
            const now = new Date();
            const expiryDate = data.vipExpiresAt.toDate();
            if (now > expiryDate) {
              data.isVIP = false;
              try {
                await updateDoc(ref, { isVIP: false });
              } catch (e) {
                console.error(e);
              }
            }
          }
          setUserData(data);
          if (data.status === "blocked") {
            await signOut(auth);
            return;
          }
          const newRole = data.role || "student";
          if (newRole !== prevRoleRef.current) {
            prevRoleRef.current = newRole;
            setUserRole(newRole);
          }
          if (!fcmInitializedRef.current) {
            fcmInitializedRef.current = true;
            initFCM(user.uid);
          }
        } else {
          setUserData({});
          if ("student" !== prevRoleRef.current) {
            prevRoleRef.current = "student";
            setUserRole("student");
          }
          if (!fcmInitializedRef.current) {
            fcmInitializedRef.current = true;
            initFCM(user.uid);
          }
        }
        setAuthLoading(false);
      }, (error) => {
        console.error("Error fetching user role:", error);
        if ("student" !== prevRoleRef.current) {
          prevRoleRef.current = "student";
          setUserRole("student");
        }
        setUserData({});
        setAuthLoading(false);
      });
    });
    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
      adminUnsubsRef.current.forEach((unsub) => unsub());
    };
  }, []);

  const value = useMemo(() => ({
    currentUser, userRole, userData, authLoading,
    adminSettings, adminSettingsLoading, loadAdminSettings
  }), [currentUser, userRole, userData, authLoading, adminSettings, adminSettingsLoading, loadAdminSettings]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);